import type { Message, ChatContext } from '../../../types/models';
import { APIError } from '../../error/errorHandler';
import type { Part, ModelParams, TextPart } from './types';
import { usageService } from '../../../services/UsageService';
import { useStore } from '../../../store/useStore';
import { getProviderConfig } from '../../../config/modelsDatabase';
import { readEnvFile, getProviderEnvKey } from '../../envManager';

// Link to get API key from Google AI Studio
export const GEMINI_API_KEY_LINK = "https://makersuite.google.com/app/apikey";

interface FileData {
  mime_type: string;
  data: string;
}

function validateApiKey(apiKey: string): void {
  if (!apiKey) {
    throw new APIError('API key is required', 'Gemini');
  }
}

// Helper function to estimate token count from text
function estimateTokenCount(text: string): number {
  // Rough estimation: ~4 characters per token on average
  return Math.ceil(text.length / 4);
}

export async function sendMessage(
  message: string,
  apiKey: string,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[],
  file?: FileData
): Promise<string> {
  try {
    // Get Gemini configuration
    const config = getProviderConfig('google');
    if (!config) {
      throw new Error('Gemini provider configuration not found');
    }

    // Check for API key
    if (!apiKey) {
      const envVars = readEnvFile();
      const systemApiKey = envVars[getProviderEnvKey('gemini')] || import.meta.env.SYSTEM_GEMINI_API_KEY;
      if (!systemApiKey) {
        throw new APIError('No API key available for Gemini', 'Gemini');
      }
      console.log('Using system Gemini API key');
      apiKey = systemApiKey;
    } else {
      console.log('Using user-provided Gemini API key');
    }

    // Log API key status for debugging (without exposing the key)
    console.log('Gemini API key status:', {
      hasKey: Boolean(apiKey),
      keyLength: apiKey?.length || 0
    });

    // Validate API key before making the request
    validateApiKey(apiKey);

    // Get the API endpoint from the provider config
    const baseUrl = config.baseUrl;
    const endpoint = config.endpoints?.chat || '/api/gemini-chat';
    const apiUrl = `${baseUrl}${endpoint}`;

    // Format messages for proxy request format
    // Convert to standard message format instead of Google's special format
    const standardMessages = [];
    
    // Add system message if available
    if (context.systemPrompt) {
      standardMessages.push({
        role: 'system',
        content: context.systemPrompt
      });
    }
    
    // Add previous messages
    previousMessages.forEach(msg => {
      standardMessages.push({
        role: msg.role,
        content: msg.content
      });
    });

    // Add the new message
    standardMessages.push({
      role: 'user',
      content: message
    });

    // Build the proxy request
    const requestBody = {
      model: modelId,
      messages: standardMessages,
      temperature: 0.7,
      max_tokens: 1024
    };

    // Make the request through our proxy
    console.log('Sending request to Gemini proxy endpoint:', apiUrl);
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Gemini proxy error:', errorData);
      throw new Error(
        typeof errorData.message === 'string' 
          ? errorData.message 
          : 'Failed to get response from Gemini'
      );
    }

    // Parse the response
    const result = await response.json();
    const text = result.choices?.[0]?.message?.content || '';

    if (!text) {
      throw new Error('Empty response from Gemini');
    }

    // Get prompt text for token estimation from standardMessages
    const promptText = standardMessages
      .filter(msg => msg.role === 'user')
      .map(msg => msg.content)
      .join('\n');
    const completionText = text;

    // Estimate token usage since Gemini doesn't provide exact counts
    const promptTokens = estimateTokenCount(promptText);
    const completionTokens = estimateTokenCount(completionText);

    // Record estimated usage
    const estimatedUsage = {
      prompt_tokens: promptTokens,
      completion_tokens: completionTokens,
      total_tokens: promptTokens + completionTokens
    };

    console.log('Gemini estimated usage:', {
      modelId,
      usage: estimatedUsage,
      promptText,
      completionText
    });

    // Get current user and topUpValue from store
    const { user, topUpValue } = useStore.getState();
    if (!user) {
      throw new Error('User not authenticated');
    }

    await usageService.recordUsage(user.uid, modelId, estimatedUsage, topUpValue);

    return text;
  } catch (error: any) {
    console.error('Gemini API Error:', error);

    // Handle specific Google API errors
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();

      if (errorMessage.includes('api key not valid') || errorMessage.includes('api_key_invalid')) {
        throw new APIError(
          'Invalid API key. Please make sure you\'re using a valid Gemini API key from Google AI Studio.',
          'Gemini'
        );
      }

      if (errorMessage.includes('permission') || errorMessage.includes('unauthorized')) {
        throw new APIError(
          'API key does not have permission to access this model. Please check your API key permissions.',
          'Gemini'
        );
      }

      if (errorMessage.includes('quota') || errorMessage.includes('rate limit')) {
        throw new APIError(
          'API quota exceeded or rate limit reached. Please try again later.',
          'Gemini'
        );
      }

      if (errorMessage.includes('location') || errorMessage.includes('not supported for the api use')) {
        throw new APIError(
          'Your location is not currently supported for direct API access. Please use a VPN or contact support for assistance.',
          'Gemini'
        );
      }

      if (errorMessage.includes('not found') || errorMessage.includes('not supported')) {
        throw new APIError(
          'Model not available or not supported. Please check if you have access to this model.',
          'Gemini'
        );
      }
    }

    // Generic error
    throw new APIError(
      error instanceof Error ? error.message : 'Failed to communicate with Gemini',
      'Gemini'
    );
  }
}